import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { interviewAPI } from '../services/api';

function MyInterviews() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list' or 'all'
  
  useEffect(() => {
    fetchInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await interviewAPI.getMyInterviews('');
      setInterviews(response.data.interviews || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
    }
    setLoading(false);
  };

  const categorizeInterviews = () => {
    const now = new Date();
    const upcoming = [];
    const ongoing = [];
    const completed = [];

    interviews.forEach(interview => {
      const startDate = new Date(interview.startDate);
      const endDate = new Date(interview.endDate);

      if (now < startDate) {
        upcoming.push(interview);
      } else if (now >= startDate && now <= endDate) {
        ongoing.push(interview);
      } else {
        completed.push(interview);
      }
    });

    return { upcoming, ongoing, completed };
  };

  const getRemainingDays = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Expired', color: '#e53e3e' };
    } else if (diffDays === 0) {
      return { text: 'Ending today', color: '#dd6b20' };
    } else if (diffDays === 1) {
      return { text: 'Today', color: '#dd6b20' };
    } else if (diffDays === 2) {
      return { text: 'Tomorrow', color: '#d69e2e' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays} days left`, color: '#d69e2e' };
    } else {
      return { text: `${diffDays} days left`, color: '#38a169' };
    }
  };

  const getStatusInfo = (interview) => {
    const now = new Date();
    const startDate = new Date(interview.startDate);
    const endDate = new Date(interview.endDate);
    const attemptNumber = interview.attemptNumber || 0;
    const maxAttempts = interview.maxAttempts !== undefined ? interview.maxAttempts : 1;

    if ((interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') && !interview.allowMultipleTakes) {
      return { text: interview.myStatus === 'submitted' ? 'Submitted' : 'Timed Out', class: `status-${interview.myStatus.replace('_', '-')}`, canTake: false, attemptNumber, maxAttempts };
    } else if ((interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') && interview.allowMultipleTakes && now >= startDate && now <= endDate) {
      const canRetake = maxAttempts === 0 || attemptNumber < maxAttempts;
      if (canRetake) {
        return { text: 'Retake Available', class: 'status-retake', canTake: true, attemptNumber, maxAttempts };
      } else {
        return { text: 'Max Attempts Reached', class: 'status-submitted', canTake: false, attemptNumber, maxAttempts };
      }
    } else if (now < startDate) {
      return { text: 'Upcoming', class: 'status-upcoming', canTake: false, attemptNumber, maxAttempts };
    } else if (now > endDate) {
      return { text: 'Expired', class: 'status-expired', canTake: false, attemptNumber, maxAttempts };
    } else if (interview.myStatus === 'in_progress') {
      return { text: 'In Progress', class: 'status-in-progress', canTake: true, attemptNumber, maxAttempts };
    } else {
      return { text: 'Available', class: 'status-available', canTake: true, attemptNumber, maxAttempts };
    }
  };

  const renderInterviewCard = (interview) => {
    const statusInfo = getStatusInfo(interview);
    
    return (
      <div 
        key={interview._id}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          transition: 'all 0.2s',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(102,126,234,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <h3 
              style={{ 
                margin: 0, 
                color: '#2d3748', 
                fontSize: '16px', 
                fontWeight: 600,
                flex: 1,
                cursor: 'pointer'
              }}
              onClick={() => navigate(`/interview-details/${interview._id}`)}
            >
              {interview.title}
            </h3>
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              marginLeft: '8px',
              background: statusInfo.class === 'status-upcoming' ? '#e7f3ff' :
                        statusInfo.class === 'status-available' ? '#c6f6d5' :
                        statusInfo.class === 'status-in-progress' ? '#feebc8' :
                        statusInfo.class === 'status-submitted' ? '#bee3f8' :
                        statusInfo.class === 'status-retake' ? '#feebc8' :
                        statusInfo.class === 'status-expired' ? '#e2e8f0' : '#fed7d7',
              color: statusInfo.class === 'status-upcoming' ? '#0066cc' :
                    statusInfo.class === 'status-available' ? '#22543d' :
                    statusInfo.class === 'status-in-progress' ? '#744210' :
                    statusInfo.class === 'status-submitted' ? '#2c5282' :
                    statusInfo.class === 'status-retake' ? '#744210' :
                    statusInfo.class === 'status-expired' ? '#2d3748' : '#742a2a'
            }}>
              {statusInfo.text}
            </span>
          </div>
          
          {interview.description && (
            <p style={{ 
              color: '#718096', 
              fontSize: '13px', 
              margin: '8px 0 0 0',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {interview.description}
            </p>
          )}
        </div>

        <div style={{ 
          background: '#f7fafc', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '12px',
          fontSize: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#718096' }}>📅 Start</span>
            <span style={{ color: '#2d3748', fontWeight: 500 }}>
              {new Date(interview.startDate).toLocaleDateString()} {new Date(interview.startDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#718096' }}>🏁 End</span>
            <span style={{ color: '#2d3748', fontWeight: 500 }}>
              {new Date(interview.endDate).toLocaleDateString()} {new Date(interview.endDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#718096' }}>⏳ Time Remaining</span>
            <span style={{ color: getRemainingDays(interview.endDate).color, fontWeight: 600 }}>
              {getRemainingDays(interview.endDate).text}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#718096' }}>⏰ Duration</span>
            <span style={{ color: '#2d3748', fontWeight: 500 }}>{interview.duration} min</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#718096' }}>❓ Questions</span>
            <span style={{ color: '#2d3748', fontWeight: 500 }}>{interview.questions?.length || 0}</span>
          </div>
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', gap: '8px' }}>
          {statusInfo.canTake ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/take-interview/${interview._id}`);
              }}
              style={{
                flex: 1,
                padding: '10px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#5568d3'}
              onMouseLeave={(e) => e.target.style.background = '#667eea'}
            >
              {interview.myStatus === 'in_progress' ? 'Continue' : 
               (interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') ? 'Retake' : 
               'Start'}
            </button>
          ) : null}
          {interview.myStatus && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/interview-details/${interview._id}`);
              }}
              style={{
                flex: statusInfo.canTake ? 'none' : 1,
                padding: '10px',
                background: 'white',
                color: '#667eea',
                border: '1px solid #667eea',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: statusInfo.canTake ? '80px' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f7fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
              }}
            >
              Details
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderListRow = (interview) => {
    const statusInfo = getStatusInfo(interview);
    const remainingDays = getRemainingDays(interview.endDate);
    
    return (
      <div 
        key={interview._id}
        style={{
          background: 'white',
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          cursor: 'pointer',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
        onClick={() => navigate(`/interview-details/${interview._id}`)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ margin: 0, color: '#2d3748', fontSize: '15px', fontWeight: 600 }}>
            {interview.title}
          </h4>
          {interview.description && (
            <p style={{ 
              margin: '4px 0 0 0', 
              color: '#718096', 
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {interview.description}
            </p>
          )}
        </div>
        
        <div style={{ fontSize: '12px', color: '#718096', minWidth: '100px' }}>
          <div style={{ fontWeight: 500, color: '#2d3748' }}>
            {new Date(interview.startDate).toLocaleDateString()}
          </div>
          <div style={{ fontSize: '11px', marginTop: '2px' }}>Start</div>
        </div>
        
        <div style={{ fontSize: '12px', color: '#718096', minWidth: '100px' }}>
          <div style={{ fontWeight: 500, color: '#2d3748' }}>
            {new Date(interview.endDate).toLocaleDateString()}
          </div>
          <div style={{ fontSize: '11px', marginTop: '2px' }}>End</div>
        </div>
        
        <div style={{ fontSize: '12px', minWidth: '100px' }}>
          <div style={{ fontWeight: 600, color: remainingDays.color }}>
            {remainingDays.text}
          </div>
          <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>Remaining</div>
        </div>
        
        <div style={{ fontSize: '13px', color: '#718096', minWidth: '80px', textAlign: 'center' }}>
          {interview.duration} min
        </div>
        
        <div style={{ minWidth: '120px', textAlign: 'center' }}>
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            background: statusInfo.class === 'status-upcoming' ? '#e7f3ff' :
                      statusInfo.class === 'status-available' ? '#c6f6d5' :
                      statusInfo.class === 'status-in-progress' ? '#feebc8' :
                      statusInfo.class === 'status-submitted' ? '#bee3f8' :
                      statusInfo.class === 'status-retake' ? '#feebc8' :
                      statusInfo.class === 'status-expired' ? '#e2e8f0' : '#fed7d7',
            color: statusInfo.class === 'status-upcoming' ? '#0066cc' :
                  statusInfo.class === 'status-available' ? '#22543d' :
                  statusInfo.class === 'status-in-progress' ? '#744210' :
                  statusInfo.class === 'status-submitted' ? '#2c5282' :
                  statusInfo.class === 'status-retake' ? '#744210' :
                  statusInfo.class === 'status-expired' ? '#2d3748' : '#742a2a'
          }}>
            {statusInfo.text}
          </span>
        </div>
        
        <div style={{ minWidth: '140px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {statusInfo.canTake && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/take-interview/${interview._id}`);
              }}
              style={{
                padding: '6px 16px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {interview.myStatus === 'in_progress' ? 'Continue' : 'Start'}
            </button>
          )}
          {interview.myStatus && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/interview-details/${interview._id}`);
              }}
              style={{
                padding: '6px 16px',
                background: 'white',
                color: '#667eea',
                border: '1px solid #667eea',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              View
            </button>
          )}
        </div>
      </div>
    );
  };

  const { upcoming, ongoing, completed } = categorizeInterviews();

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '8px', fontWeight: 700 }}>My Interviews</h2>
          <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
            View and take your assigned interviews
          </p>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '12px'
        }}>
          <button
            onClick={() => setViewMode('cards')}
            style={{
              padding: '8px 16px',
              background: viewMode === 'cards' ? '#667eea' : 'white',
              color: viewMode === 'cards' ? 'white' : '#667eea',
              border: `1px solid ${viewMode === 'cards' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📊 By Category
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 16px',
              background: viewMode === 'list' ? '#667eea' : 'white',
              color: viewMode === 'list' ? 'white' : '#667eea',
              border: `1px solid ${viewMode === 'list' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📋 All Interviews
          </button>
        </div>

        {loading ? (
          <div>
            {/* View Mode Buttons Skeleton */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '24px'
            }}>
              {[1, 2, 3].map(i => (
                <Skeleton 
                  key={i}
                  variant="rectangular" 
                  width={150} 
                  height={36} 
                  sx={{ borderRadius: '6px' }} 
                />
              ))}
            </div>

            {/* Category Sections Skeleton */}
            {[1, 2].map(section => (
              <div key={section} style={{ marginBottom: '32px' }}>
                {/* Section Header */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton variant="text" width={200} height={24} />
                </div>
                
                {/* Interview Cards Grid */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '20px'
                }}>
                  {[1, 2, 3].map(card => (
                    <div
                      key={card}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      {/* Title and Status Badge */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '12px' }} />
                      </div>

                      {/* Description */}
                      <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="75%" height={16} sx={{ mb: 2 }} />

                      {/* Date and Duration Grid */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        <div>
                          <Skeleton variant="text" width="60%" height={14} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="80%" height={16} />
                        </div>
                        <div>
                          <Skeleton variant="text" width="60%" height={14} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="70%" height={16} />
                        </div>
                      </div>

                      {/* Questions Count */}
                      <Skeleton variant="text" width="50%" height={14} sx={{ mb: 2 }} />

                      {/* Attempt Info */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                      }}>
                        <Skeleton variant="text" width="40%" height={14} />
                        <Skeleton variant="text" width="30%" height={14} />
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Skeleton variant="rectangular" width="60%" height={38} sx={{ borderRadius: '6px' }} />
                        <Skeleton variant="rectangular" width="38%" height={38} sx={{ borderRadius: '6px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : interviews.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: '#f7fafc',
            borderRadius: '12px',
            color: '#718096'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <p style={{ fontSize: '16px', fontWeight: 500 }}>No interviews assigned to you yet.</p>
          </div>
        ) : viewMode === 'cards' ? (
          <>
            {/* Ongoing Interviews */}
            {ongoing.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ 
                  color: '#2d3748', 
                  fontSize: '18px', 
                  fontWeight: 600,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#48bb78' 
                  }}></span>
                  Ongoing Interviews ({ongoing.length})
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '20px'
                }}>
                  {ongoing.map(interview => renderInterviewCard(interview))}
                </div>
              </div>
            )}

            {/* Upcoming Interviews */}
            {upcoming.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ 
                  color: '#2d3748', 
                  fontSize: '18px', 
                  fontWeight: 600,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#4299e1' 
                  }}></span>
                  Upcoming Interviews ({upcoming.length})
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '20px'
                }}>
                  {upcoming.map(interview => renderInterviewCard(interview))}
                </div>
              </div>
            )}

            {/* Completed Interviews */}
            {completed.length > 0 && (
              <div>
                <h3 style={{ 
                  color: '#2d3748', 
                  fontSize: '18px', 
                  fontWeight: 600,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#718096' 
                  }}></span>
                  Completed Interviews ({completed.length})
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '20px'
                }}>
                  {completed.map(interview => renderInterviewCard(interview))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <div style={{ 
              display: 'flex',
              padding: '12px 20px',
              background: '#f7fafc',
              borderBottom: '2px solid #e2e8f0',
              fontSize: '12px',
              fontWeight: 600,
              color: '#718096',
              textTransform: 'uppercase'
            }}>
              <div style={{ flex: 1 }}>Interview</div>
              <div style={{ minWidth: '140px' }}>Date</div>
              <div style={{ minWidth: '80px', textAlign: 'center' }}>Duration</div>
              <div style={{ minWidth: '120px', textAlign: 'center' }}>Status</div>
              <div style={{ minWidth: '140px', textAlign: 'right' }}>Actions</div>
            </div>
            {interviews.map(interview => renderListRow(interview))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyInterviews;
